import { type Incident, IncidentType } from '@cobliteam/api-lib';
import { useGeocode } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';

import { Tag } from '@cobliteam/ui/components';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import type React from 'react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  TrackingPopUp,
  TrackingPopUpContent,
  TrackingPopUpFooter,
  TrackingPopUpHeader,
  TrackingPopUpHeaderMain,
  TrackingPopUpInfoRowText,
  TrackingPopUpSkeleton,
  TrackingPopUpTitle,
} from '../Tracking/components/Common/TrackingPopUp';
import { type IncidentCluster, IncidentIconComponent } from '../Tracking/components/Map';
import { formatTimestamp } from '../Tracking/utils/date';

export interface IncidentPopupProps {
  incidentOrCluster: Incident | IncidentCluster;
  driverName?: string;
  duration?: number;
  children?: ReactNode;
}

export const IncidentPopup: React.FC<IncidentPopupProps> = ({ incidentOrCluster: incident, driverName, children }) => {
  const { t } = useTranslation('tracking');
  const { getAddress } = useGeocode();
  const [address, setAddress] = useState('');

  const isCluster = 'incidents' in incident;
  const { type, latitude, longitude, time } = incident;

  const devicePlugged = type === IncidentType.DEVICE_PLUGGED;
  const deviceStatusText = devicePlugged ? t('connected_at') : t('disconnected_at');
  const title = devicePlugged ? t('device_connected') : t('device_disconnected');

  const handleAddress = useCallback(async () => {
    if (!(latitude && longitude)) return;

    const latLng = { lat: latitude, lng: longitude };
    const responseAddress = await getAddress(latLng);
    setAddress(responseAddress);
  }, [latitude, longitude, getAddress]);

  useEffect(() => {
    handleAddress();
  }, [handleAddress]);

  return (
    <TrackingPopUp>
      <TrackingPopUpHeader>
        <TrackingPopUpHeaderMain>
          <IncidentIconComponent type={incident.type} />
          <TrackingPopUpTitle>{title}</TrackingPopUpTitle>
        </TrackingPopUpHeaderMain>
        {isCluster && <Tag icon={<Icon name='triangle-alert' />} color='warning' label={`${incident.incidents.length} ${t('events')}`} />}
      </TrackingPopUpHeader>

      <TrackingPopUpContent>
        {isCluster && (
          <div className='flex items-center gap-1.5'>
            <Icon name='triangle-alert' color='warning' />
            <Typography variant='caption-default'>{t('check_device_connection')}</Typography>
          </div>
        )}
        <TrackingPopUpInfoRowText title={deviceStatusText}>{formatTimestamp(time)}</TrackingPopUpInfoRowText>
        <TrackingPopUpInfoRowText title={t('address')}>{address || <TrackingPopUpSkeleton />}</TrackingPopUpInfoRowText>
        {driverName && <TrackingPopUpInfoRowText title={t('driver')}>{driverName}</TrackingPopUpInfoRowText>}

        <TrackingPopUpFooter>{children}</TrackingPopUpFooter>
      </TrackingPopUpContent>
    </TrackingPopUp>
  );
};
